/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {removeTask} from '@ion/core/src/services/Connect';
import {CreateJob} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseCancelTaskResult = UseDefaultQueryResult & {
  cancelTask: (job: CreateJob, index: number) => void;
}

const api = async({job, index}) => {
  try {
    const requestBody = '"Cancelling"';
    const result      = await removeTask(job?.jobId, index, requestBody);
    return result;
  } catch (err) {
    error(err);
    throw new Error(`An ${err.status || ''} Error Occurred`);
  }
};

/**
 * Common hook for cancelling a Task level Job.
 *
 * ```typescript
 *
 *    import useCancelTask from '@ion/query/src/hooks/';
 *
 *    const {resendShipment, isSuccess, isError} = useCancelTask();
 *
 * ```
 */
export const useCancelTask = (): UseCancelTaskResult => {
  const queryClient = useQueryClient();
  const mutation    = useMutation(api, {
    onSettled : () => {
      queryClient.invalidateQueries([QueryKey.CANCEL_TASK]);
    }
  });

  const cancelTask = useCallback((job: CreateJob, index: number) => {
    mutation.mutate({job, index});
  }, [mutation]);

  return createMutationResult(
    mutation, {cancelTask}
  ) as UseCancelTaskResult;
};

export default useCancelTask;
